/**
 * Member Incentive Dashboard
 * Displays member's earned incentives, actions, achievements, and debit card balance
 * Trust Marketplace - Incentive Tracking System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  DollarSign,
  Star,
  Trophy,
  TrendingUp,
  CreditCard,
  Award,
  CheckCircle,
  Clock,
  Gift
} from 'lucide-react';

interface MemberIncentiveDashboardProps {
  memberId: number;
}

interface IncentiveSummary {
  actions: {
    total: number;
    completed: number;
    total_points: number;
    potential_earnings: number;
  };
  earnings: {
    total_earnings: number;
    total_paid: number;
    pending_amount: number;
    total_points_earned: number;
  };
  recent_achievements: Array<{
    type: string;
    name: string;
    level: string | null;
    points: number;
    earned_date: string;
  }>;
}

const MemberIncentiveDashboardComponent: React.FC<MemberIncentiveDashboardProps> = ({ memberId }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [summary, setSummary] = useState<IncentiveSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchIncentiveSummary();
  }, [memberId]);

  const fetchIncentiveSummary = async () => {
    try {
      setLoading(true);

      const token = getMemberToken();
      if (!token) {
        throw new Error('Not authenticated. Please log in again.');
      }

      // Member portal auth: fetch with the MEMBER token. authFetch sends the
      // staff `auth_token` (absent in a member session) → 401 error banner.
      const response = await fetch(getWeaveinApiUrl(`/api/incentives/members/${memberId}/summary`), {
        headers: {
          Authorization: `Bearer ${token}`,
          'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        // Members without an incentive program (e.g. CBO-only members, or an
        // org that hasn't set up incentives) get a 4xx/5xx here — that's not an
        // error to the member, just "nothing yet". Render an empty zero-state
        // rather than a red error banner.
        setSummary({
          actions: { total: 0, completed: 0, total_points: 0, potential_earnings: 0 },
          earnings: {
            total_earnings: 0,
            total_paid: 0,
            pending_amount: 0,
            total_points_earned: 0,
          },
          recent_achievements: [],
        });
        return;
      }

      const data = await response.json();
      setSummary(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return fmt.medium(dateString);
  };

  const getAchievementIcon = (type: string) => {
    switch (type) {
      case 'BADGE':
        return <Award className="w-5 h-5" />;
      case 'LEVEL':
        return <TrendingUp className="w-5 h-5" />;
      case 'MILESTONE':
        return <Trophy className="w-5 h-5" />;
      default:
        return <Star className="w-5 h-5" />;
    }
  };

  const getAchievementColor = (level: string | null) => {
    switch (level?.toLowerCase()) {
      case 'platinum':
        return 'bg-gray-300 text-gray-900';
      case 'gold':
        return 'bg-yellow-400 text-yellow-900';
      case 'silver':
        return 'bg-gray-400 text-gray-900';
      case 'bronze':
        return 'bg-amber-600 text-white';
      default:
        return 'bg-blue-100 text-blue-900';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error: {error}</p>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const completionRate = summary.actions.total > 0
    ? Math.round((summary.actions.completed / summary.actions.total) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Earned */}
        <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">{t('incentives.total_earned', 'Total Earned')}</p>
              <p className="text-3xl font-bold text-green-900 mt-2">
                {formatCurrency(summary.earnings.total_paid)}
              </p>
            </div>
            <div className="bg-green-500 rounded-full p-3">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        {/* Pending Earnings */}
        <Card className="p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">{t('incentives.pending', 'Pending')}</p>
              <p className="text-3xl font-bold text-yellow-900 mt-2">
                {formatCurrency(summary.earnings.pending_amount)}
              </p>
            </div>
            <div className="bg-yellow-500 rounded-full p-3">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        {/* Total Points */}
        <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">{t('incentives.total_points', 'Total Points')}</p>
              <p className="text-3xl font-bold text-purple-900 mt-2">
                {summary.earnings.total_points_earned.toLocaleString()}
              </p>
            </div>
            <div className="bg-purple-500 rounded-full p-3">
              <Star className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>

        {/* Completed Actions */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">{t('incentives.actions_completed', 'Actions Completed')}</p>
              <p className="text-3xl font-bold text-blue-900 mt-2">
                {summary.actions.completed}
                <span className="text-sm text-blue-600 ml-1">/ {summary.actions.total}</span>
              </p>
              <div className="mt-2">
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${completionRate}%` }}
                  />
                </div>
                <p className="text-xs text-blue-600 mt-1">{completionRate}% Complete</p>
              </div>
            </div>
            <div className="bg-blue-500 rounded-full p-3">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* Action Progress & Achievements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Action Progress */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('incentives.your_progress', 'Your Progress')}</h3>
          </div>

          <div className="space-y-4">
            {/* Total Incentive Earnings */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{t('incentives.total_incentive_earnings', 'Total Incentive Earnings')}</span>
              <Badge variant="default" className="bg-green-100 text-green-800">
                {summary.earnings.total_earnings} incentives
              </Badge>
            </div>

            {/* Potential Earnings */}
            <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
              <span className="text-sm font-medium text-yellow-800">
                {t('incentives.potential_earnings_from_pending_actions', 'Potential Earnings from Pending Actions')}
              </span>
              <span className="text-lg font-bold text-yellow-900">
                {formatCurrency(summary.actions.potential_earnings)}
              </span>
            </div>

            {/* Action Breakdown */}
            <div className="pt-4 border-t">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">{t('incentives.completed_actions', 'Completed Actions')}</span>
                <span className="text-sm font-semibold text-gray-900">
                  {summary.actions.completed}
                </span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">{t('incentives.in_progress', 'In Progress')}</span>
                <span className="text-sm font-semibold text-gray-900">
                  {summary.actions.total - summary.actions.completed}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">{t('incentives.points_available', 'Points Available')}</span>
                <span className="text-sm font-semibold text-gray-900">
                  {summary.actions.total_points.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Recent Achievements */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">{t('incentives.recent_achievements', 'Recent Achievements')}</h3>
          </div>

          <div className="space-y-3">
            {summary.recent_achievements.length === 0 ? (
              <div className="text-center py-8">
                <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">{t('incentives.no_achievements_yet', 'No achievements yet')}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {t('incentives.complete_actions_to_earn_achievements', 'Complete actions to earn achievements!')}
                </p>
              </div>
            ) : (
              summary.recent_achievements.map((achievement, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className={`rounded-full p-2 ${getAchievementColor(achievement.level)}`}>
                    {getAchievementIcon(achievement.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {achievement.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {achievement.level && (
                        <Badge
                          variant="outline"
                          className={`text-xs ${getAchievementColor(achievement.level)}`}
                        >
                          {achievement.level}
                        </Badge>
                      )}
                      <span className="text-xs text-gray-500">
                        +{achievement.points} pts
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDate(achievement.earned_date)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {summary.recent_achievements.length > 0 && (
            <button className="w-full mt-4 px-4 py-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors">
              {t('incentives.view_all_achievements', 'View All Achievements →')}
            </button>
          )}
        </Card>
      </div>

      {/* Next Steps / Call to Action */}
      {summary.earnings.pending_amount > 0 && (
        <Card className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <div className="flex items-start gap-4">
            <div className="bg-blue-500 rounded-full p-3">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-1">
                {t('incentives.you_have_pending_earnings', 'You have pending earnings!')}
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                Complete verification steps to receive {formatCurrency(summary.earnings.pending_amount)} in incentives.
              </p>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                {t('incentives.view_pending_earnings', 'View Pending Earnings')}
              </button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

// Export directly for member portal (no capability gate needed for member-facing views)
// Members should always be able to see their own earned incentives
export default MemberIncentiveDashboardComponent;
